import {
  APP_SHELL_RAIL_TILE_CHIP_SIZE,
  AppShellRailTile,
  Box,
  PreviewCard,
  PreviewCardPopup,
  Separator,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useElementSize,
  useIsMobile,
  useMediaQuery,
} from "@salestrak/ui";
import * as React from "react";
import { InviteMembersDialog } from "#app/components/members/invite-members-dialog";
import { useCanInvite } from "#app/lib/hooks/use-can-invite";
import { SETTINGS_BROWSE_ID, useModuleRail } from "./module-rail-context";
import { MoreModulesTile } from "./more-modules-tile";
import { useModuleTilePress } from "./use-module-tile-press";
import { useInboxUnreadCount } from "#app/hooks/inbox/use-inbox-unread-count";
import {
  useExpandSidebar,
  useSidebarCollapsed,
} from "#app/lib/hooks/use-sidebar-collapsed";
import { useIsSettingsRoute } from "#app/lib/hooks/use-is-settings-route";
import { SidebarToggleTooltipLabel } from "#app/components/navigation/collapse-sidebar-button";
import { GlobalSearchTooltipLabel } from "#app/components/global-search/global-search-button";
import { useGlobalSearchActions } from "#app/components/global-search/global-search-context";
import { AccountMenu } from "#app/components/navigation/account-menu";
import { ModuleMenuForTile } from "#app/components/navigation/module-menu-for-tile";
import { SettingsSidebar } from "#app/components/navigation/settings-sidebar";
import { ModuleTooltipBody } from "./module-tooltip-body";
import { WorkspaceRailTile } from "./workspace-rail-tile";

/**
 * ModuleRail — narrow vertical navigation rail (far-left column of the shell).
 *
 * Tile order + visibility come from the user's `railTiles` preference,
 * resolved against the static registry in `useNavPreferences` and shared via
 * `ModuleRailContext`. Hidden tiles move to the More popup. "Invite" is
 * permission-gated and opens InviteMembersDialog.
 *
 * On desktop the rail is also the shell's whole chrome — there is no site
 * header — so it bookends the modules with the two identity marks: the
 * workspace at the head, the account at the foot. Both are uncaptioned 32px
 * chips in either rail mode. Inside the mobile drawer the rail sits under
 * `SiteHeader`, which already carries both, so it renders neither.
 */

/**
 * The hover flyout's panel body plus its caret. The panel holds ONE static
 * spot for every tile, so the caret is the one thing saying WHICH tile owns
 * the menu on screen: a notch on the panel's left edge at the hovered tile's
 * centerline, measured when the popup mounts. Base UI's own arrow part can't
 * draw this — it points at the positioning anchor, which is the rail, not
 * the tile.
 */
function FlyoutPanel({
  tileEl,
  children,
}: {
  tileEl: HTMLElement | null | undefined;
  children: React.ReactNode;
}) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [caretTop, setCaretTop] = React.useState<number | null>(null);
  React.useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel || !tileEl) return;
    const tile = tileEl.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    // Kept inside the panel's rounded edge: the panel ends above the rail's
    // foot, so a tile hovered below its bottom edge would otherwise hang a
    // caret in the gap under the card.
    const inset = 12;
    setCaretTop(
      Math.min(
        Math.max(tile.top + tile.height / 2 - panelRect.top - 6, inset),
        panelRect.height - inset - 12,
      ),
    );
  }, [tileEl]);
  return (
    <Box direction="column" fill position="relative" ref={panelRef}>
      <Box direction="column" grow minHeight={0} overflow="hidden" rounded="lg">
        {children}
      </Box>
      {caretTop != null && (
        <Box
          // A rotated square whose left+bottom edges form the point. Its fill
          // is `sidebar`, NOT the popup's popover: the menu inside the panel
          // paints the same chrome fill it wears as the real pane, so a
          // popover-filled caret reads one tone brighter than the surface it
          // claims to be part of. It vanishes the instant the popup starts
          // closing (`in-data-ending-style`): the panel's exit fade turns
          // near-invisible against the page while the caret's outline stays
          // legible, and a caret outliving its panel reads as debris.
          className="rotate-45 rounded-[2px] border-b border-l bg-sidebar transition-opacity in-data-starting-style:opacity-0 in-data-ending-style:opacity-0"
          height={12}
          left={-6}
          position="absolute"
          top={caretTop}
          width={12}
        />
      )}
    </Box>
  );
}

// Rail capacity is measured, not capped: the rail tracks its own height and
// the (always-rendered) More tile's height, then fits as many shown tiles as
// the column actually holds. Shown tiles past the fit — and the Invite tile
// when even it no longer fits — overflow into the More popup, exactly like
// user-hidden tiles. The More tile itself always renders.
//
// Geometry the fit math can't measure live: a divider block (1px line + xs
// padding), the collapsed-pane controls (open + search, plus the Settings
// module tile on a settings route — their wrapper leads with a divider-sized
// breath and holds the internal gaps inside its own height, so N tiles cost
// a divider, N tiles and N+1 gap slots), and the two identity chips. Identity and the open/search controls use the 32px default
// chip; the Settings tile is a module tile at the measured module height. Module tiles and high-priority
// actions use the 36px large chip. Every fixed action is icon-only, so its
// height does not track compact mode. The identity chips each pair with a
// divider-sized breath (the head slot below the workspace mark, the foot slot
// above the account mark). The column gap is mode-dependent: 8px compact and
// 12px expanded. Keep the fit math and JSX on the shared `railGap` value below.
const RAIL_DIVIDER_HEIGHT = 9;
const RAIL_CHIP_HEIGHT = APP_SHELL_RAIL_TILE_CHIP_SIZE.default;
const MODULE_CHIP_SIZE = APP_SHELL_RAIL_TILE_CHIP_SIZE.lg;
const PRIMARY_ACTION_CHIP_SIZE = APP_SHELL_RAIL_TILE_CHIP_SIZE.lg;

// Hover-intent timing for the module-menu flyout. The open delay gates the
// FIRST presentation only — once the panel is up, moving to another tile swaps
// its content in place with no delay and no close/reopen, which is what keeps
// the walk down the column flicker-free. The close delay is the grace window
// for crossing the 8px gap between the rail and the panel (and the gaps
// between tiles) without the panel dropping.
const FLYOUT_OPEN_DELAY = 220;
const FLYOUT_CLOSE_DELAY = 150;

export function ModuleRail() {
  const isMobile = useIsMobile();
  // Invite visibility is grant-driven (role_invite_grant) — show when the
  // actor can invite into at least one role, NOT a permission key. Mirrors
  // the Members page and InviteMembersDialog gating.
  const canInvite = useCanInvite();
  // The compact preference is the ONLY thing that decides captions: it holds
  // at every width tier, so a user who wants labels beside the medium tier's
  // overlay pane keeps them, and one who wants the narrow rail keeps that.
  const { activeTileId, compact, countDot, railTiles } = useModuleRail();
  const inboxUnreadCount = useInboxUnreadCount();
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const handleTilePress = useModuleTilePress();
  // `useSidebarCollapsed` is already false on mobile (there, `open=false`
  // means the drawer is dismissed, not the pane collapsed), which is what the
  // rail wants in both places it reads this.
  const submenuCollapsed = useSidebarCollapsed();
  const expandSidebar = useExpandSidebar();
  // Settings has no module tile: its navigation pane replaces the module menu
  // on its routes, so with the pane collapsed nothing in the rail points at
  // it. The collapsed-pane block grows a Settings chip there, and the chip is
  // the flyout anchor for the settings pane exactly as a module tile is for
  // its menu.
  const isSettingsRoute = useIsSettingsRoute();
  // Hover is a mouse fact: on touch there is no hover to preview with, so
  // the flyout stays off and a press simply navigates.
  const canHover = useMediaQuery("(hover: hover) and (pointer: fine)");
  const menuFlyout = !isMobile && submenuCollapsed && canHover;
  // Tile elements by id — the flyout caret measures the hovered tile's
  // centerline from here.
  const tileEls = React.useRef(new Map<string, HTMLElement | null>());

  // The flyout is ONE controlled popup shared by every module tile, not a
  // popup per tile: with per-tile roots, walking from Home to Workflows closes
  // one popup (exit fade) and mounts another after its open delay — a visible
  // blink in the same static spot. Here the tiles only report hover; while the
  // panel is up, a new tile swaps the menu inside it and re-seats the caret,
  // and the panel itself never closes.
  const [flyoutTileId, setFlyoutTileId] = React.useState<string | null>(null);
  const flyoutOpenTimer = React.useRef<number | null>(null);
  const flyoutCloseTimer = React.useRef<number | null>(null);
  const clearFlyoutTimers = React.useCallback(() => {
    if (flyoutOpenTimer.current != null) {
      window.clearTimeout(flyoutOpenTimer.current);
      flyoutOpenTimer.current = null;
    }
    if (flyoutCloseTimer.current != null) {
      window.clearTimeout(flyoutCloseTimer.current);
      flyoutCloseTimer.current = null;
    }
  }, []);
  React.useEffect(() => clearFlyoutTimers, [clearFlyoutTimers]);
  // The pane expanding (or hover going away) takes the flyout with it.
  React.useEffect(() => {
    if (!menuFlyout) setFlyoutTileId(null);
  }, [menuFlyout]);

  const enterFlyoutTile = (tileId: string) => {
    clearFlyoutTimers();
    if (flyoutTileId != null) {
      // Already open: swap in place — the flicker-free path.
      setFlyoutTileId(tileId);
    } else {
      flyoutOpenTimer.current = window.setTimeout(() => {
        flyoutOpenTimer.current = null;
        setFlyoutTileId(tileId);
      }, FLYOUT_OPEN_DELAY);
    }
  };
  const scheduleFlyoutClose = () => {
    clearFlyoutTimers();
    flyoutCloseTimer.current = window.setTimeout(() => {
      flyoutCloseTimer.current = null;
      setFlyoutTileId(null);
    }, FLYOUT_CLOSE_DELAY);
  };
  const cancelFlyoutClose = () => {
    if (flyoutCloseTimer.current != null) {
      window.clearTimeout(flyoutCloseTimer.current);
      flyoutCloseTimer.current = null;
    }
  };
  // The exit fade outlives the hover: keep rendering the LAST menu while the
  // popup animates out, or the panel goes empty for its closing frames.
  const lastFlyoutTileId = React.useRef<string | null>(null);
  if (flyoutTileId != null) lastFlyoutTileId.current = flyoutTileId;
  const flyoutContentTileId = flyoutTileId ?? lastFlyoutTileId.current;
  const { openSearch } = useGlobalSearchActions();

  // Live measurements driving the overflow fit: the rail column's height and
  // the always-rendered More tile. Add the design-system chip delta for a
  // module tile, so both heights track compact mode and font changes without
  // assuming a caption height. Zero = not measured yet.
  const { ref: railRef, size: railSize } = useElementSize<HTMLDivElement>();
  const { ref: tileSlotRef, size: tileSlotSize } =
    useElementSize<HTMLDivElement>();
  const railHeight = railSize.height || null;
  const moreTileHeight = tileSlotSize.height || null;
  const moduleTileHeight = moreTileHeight
    ? moreTileHeight + MODULE_CHIP_SIZE - RAIL_CHIP_HEIGHT
    : null;

  // Rail top in viewport coords — the More popup's collision boundary, so it
  // never floats above the rail's top edge on short screens. Re-read whenever
  // the rail resizes.
  const [railTop, setRailTop] = React.useState(0);
  React.useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    const top = Math.max(0, Math.floor(el.getBoundingClientRect().top));
    setRailTop((prev) => (prev === top ? prev : top));
  }, [railRef, railHeight]);

  const shownTiles = railTiles.filter((tile) => tile.show);

  // Desktop only: when the module-menu pane was collapsed (the ModuleMenu
  // header's collapse control), show an expand control at the top of the rail.
  // On mobile the rail lives inside the drawer, so the collapsed state never
  // shows a rail to host it. Tiles without a module menu (`hasSubmenu:
  // false`, none today) are excluded: there is nothing to expand into.
  const activeTileHasSubmenu = railTiles.find((tile) => tile.id === activeTileId)?.hasSubmenu ?? true;
  // The slot exists whenever the rail could host those controls; `showExpand`
  // is whether they are currently open. The slot stays mounted while closed so
  // the reveal has something to animate (see the block in the JSX).
  const expandSlot = !isMobile && activeTileHasSubmenu;
  const showExpand = expandSlot && submenuCollapsed;
  // Chips inside the collapsed-pane block: open + search (32px chips), and
  // the Settings tile while a settings route is up. That one is a MODULE
  // tile — large chip, caption in the labelled rail — so it costs a module
  // tile's measured height, not a chip's. The block's stated height and the
  // fit math both count from here.
  const collapsedChipCount = isSettingsRoute ? 3 : 2;
  const collapsedBlockHeight =
    RAIL_DIVIDER_HEIGHT +
    2 * RAIL_CHIP_HEIGHT +
    (isSettingsRoute ? (moduleTileHeight ?? MODULE_CHIP_SIZE) : 0);

  // The identity bookends are the desktop shell's replacement for a site
  // header; inside the mobile drawer `SiteHeader` owns both.
  const showIdentity = !isMobile;
  // A divider-sized slot closes the rail's head whenever the head has anything
  // in it — the breath between the identity block and the nav tiles is part of
  // the rail's rhythm either way. The LINE inside it paints only while the
  // collapsed-pane chips are up: the workspace mark alone reads apart from the
  // nav tiles by its solid tile, and a rung under just that one chip is a line
  // with nothing to group.
  const headDividerSlot = showIdentity || showExpand;
  const showHeadDividerLine = showExpand;

  // A 32px chip leads the rail whenever the head has one — the workspace mark
  // on desktop, the expand control while the pane is collapsed. `sm` (8) then
  // seats that chip's centerline (24px) on the pane header's first-line
  // centerline (22px). Compact mode has no pane title to align to and takes
  // `sm` too. Only a labelled nav tile leading the column wants `md` (16), so
  // its top lines up with the title rather than its centerline.
  const padY = (compact || showExpand || showIdentity ? 8 : 16) + 8;
  // Everything in the column that is a fixed chip rather than a nav tile.
  // The closing divider's height is rolled in below.
  const chromeHeight =
    (showIdentity ? 2 * RAIL_CHIP_HEIGHT + RAIL_DIVIDER_HEIGHT : 0) +
    (showExpand ? collapsedBlockHeight : 0) +
    (headDividerSlot ? RAIL_DIVIDER_HEIGHT : 0) +
    RAIL_DIVIDER_HEIGHT;
  // Gap slots the chrome occupies: [ws?] [expand?] [head divider?] … [divider]
  // More [spacer] … [foot breath? account?]. The zero-height flex spacer
  // still takes one.
  const chromeChildren =
    (showIdentity ? 3 : 0) +
    (showExpand ? collapsedChipCount + 1 : 0) +
    (headDividerSlot ? 1 : 0) +
    3;
  // Keep the captioned rail one 4px step roomier than the icon-only rail.
  const railGap = compact ? 8 : 12;

  // Floor: Home + divider + More must always fit inside the rail's border.
  // Below this the column stops shrinking and overflows the viewport instead
  // of pushing the More tile outside the rail box — the least-bad failure
  // for absurdly short windows. (~60 ≈ a normal tile until measured.)
  const minRailHeight =
    padY +
    chromeHeight +
    (moduleTileHeight ?? 60) +
    (moreTileHeight ?? 60) +
    railGap * (chromeChildren + 1);

  // Fit: how many shown tiles (and whether Invite) the measured column holds.
  // Invite cedes its slot first — nav tiles take priority — then shown tiles
  // drop from the end into the More popup. Home (always first) never drops.
  // Before both measurements land (first frame) everything renders.
  let visibleCount = shownTiles.length;
  let inviteInRail = canInvite;
  if (
    railHeight != null &&
    moduleTileHeight != null &&
    moreTileHeight != null
  ) {
    const fits = (count: number, withInvite: boolean) => {
      const children = chromeChildren + count + (withInvite ? 1 : 0);
      const height =
        padY +
        chromeHeight +
        (withInvite ? PRIMARY_ACTION_CHIP_SIZE : 0) +
        count * moduleTileHeight +
        moreTileHeight +
        railGap * (children - 1);
      return height <= railHeight;
    };
    inviteInRail = canInvite && fits(shownTiles.length, true);
    if (!inviteInRail) {
      while (visibleCount > 1 && !fits(visibleCount, false)) {
        visibleCount -= 1;
      }
    }
  }

  const visibleTiles = shownTiles.slice(0, visibleCount);
  // Fit-overflowed tiles first, then the user-hidden ones.
  const overflowTiles = [...shownTiles.slice(visibleCount), ...railTiles.filter((tile) => !tile.show)];

  return (
    <Box
      align="center"
      // The shell's primary navigation landmark. It is the only nav region on
      // desktop, so it is named rather than left as an anonymous column.
      aria-label="Modules"
      as="nav"
      // No surface and no frame here: `AppShell` paints the navigation card,
      // which is what lets the rail and the module-menu pane share ONE card
      // with a hairline between them.
      direction="column"
      fill="flex"
      gap={compact ? "sm" : "base"}
      minHeight={minRailHeight}
      paddingBottom="sm"
      // No horizontal padding: gives labels the full rail width so "Workflows"
      // fits. Icons are centered so dropping the padding doesn't shift them.
      paddingX="none"
      // See `padY` above for why the top pad answers to what leads the column.
      // `sm` bottom trims dead space under the rail's pinned items.
      paddingTop={compact || showExpand || showIdentity ? "sm" : "md"}
      ref={railRef}
      rounded="lg"
    >
      {/* Head: the workspace mark, then the pane-open control while the pane
       * is collapsed, closed by one short divider. */}
      {showIdentity && <WorkspaceRailTile />}

      {/* Pane-collapsed controls: open the pane, the search action the
       * collapsed pane header took away with it, and — on a settings route —
       * the Settings chip that previews the settings pane. All are compact
       * rail tiles, so their chip, hover and rhythm are the nav tiles' by
       * construction. */}
      {expandSlot && (
        <Box
          // Closed, this block stays MOUNTED at zero height instead of
          // unmounting, so the two chips open and close the column over the
          // same 200ms the pane spends sliding out from under the card, rather
          // than popping in once it lands. Height is the animated property, so
          // it is stated (the chips + the gaps between them) rather than left
          // to content, and the negative bottom margin gives back the gap slot the
          // rail still pays for a zero-height child — `transition="space"`
          // carries both.
          aria-hidden={!showExpand || undefined}
          direction="column"
          duration={200}
          fill="width"
          gap={compact ? "sm" : "base"}
          height={
            showExpand ? collapsedBlockHeight + collapsedChipCount * railGap : 0
          }
          marginBottomNegative={showExpand ? undefined : compact ? "xs" : "sm"}
          overflow="hidden"
          transition="space"
        >
          {/* Divider-sized breath between the workspace mark and the chips —
            * space only, no line, mirroring the head slot below the chips. */}
          <Box align="center" fill="width" paddingY="xs">
            <Box height={1} />
          </Box>
          <Tooltip>
            <TooltipTrigger
              render={
                <AppShellRailTile
                  compact
                  icon="chevrons-right"
                  label="Open sidebar"
                  onClick={expandSidebar}
                  // Clipped away, not gone: keep the closed block out of the
                  // tab order.
                  tabIndex={showExpand ? undefined : -1}
                />
              }
            />
            {/* Right, into the page — a bottom bubble would sit on the tiles. */}
            <TooltipPopup side="right">
              <SidebarToggleTooltipLabel label="Open sidebar" />
            </TooltipPopup>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger
              render={
                <AppShellRailTile
                  compact
                  icon="search"
                  label="Search everything"
                  onClick={() => openSearch("rail")}
                  tabIndex={showExpand ? undefined : -1}
                />
              }
            />
            <TooltipPopup side="right">
              <GlobalSearchTooltipLabel />
            </TooltipPopup>
          </Tooltip>
          {isSettingsRoute && (() => {
            // A module tile in every respect but its home: the active chip
            // fill and selected glyph (settings IS where the user is, and the
            // route lights no other tile), the large chip, the caption in the
            // labelled rail. It lives here rather than in the module list
            // because it exists only while the pane it stands in for is away.
            // Pressing it opens that pane.
            const settingsTile = (
              <AppShellRailTile
                active
                compact={compact}
                icon="settings"
                label="Settings"
                onClick={expandSidebar}
                selectedIcon="settings-filled"
                size="lg"
                tabIndex={showExpand ? undefined : -1}
              />
            );
            // With a pointer that can hover, the chip previews the settings
            // pane in the shared flyout, replacing its name tooltip the same
            // way a module tile's menu replaces its own.
            if (menuFlyout) {
              return React.cloneElement(settingsTile, {
                ref: (el: HTMLElement | null) =>
                  tileEls.current.set(SETTINGS_BROWSE_ID, el),
                onPointerEnter: () => enterFlyoutTile(SETTINGS_BROWSE_ID),
                onPointerLeave: scheduleFlyoutClose,
                onFocus: () => enterFlyoutTile(SETTINGS_BROWSE_ID),
                onBlur: scheduleFlyoutClose,
              });
            }
            return (
              <Tooltip>
                <TooltipTrigger render={settingsTile} />
                <TooltipPopup side="right">
                  <SidebarToggleTooltipLabel label="Open settings menu" />
                </TooltipPopup>
              </Tooltip>
            );
          })()}
        </Box>
      )}

      {/* Ref detail: a short divider seats the collapsed-pane chips apart from
        * the nav tiles below. Ink at alpha, not `bg-border`: the rail canvas is the
        * deepest plane in the ramp, and in light mode every solid hairline
        * token is LIGHTER than it, so a token fill inverts and vanishes here.
        * Foreground alpha composites against whatever the rail paints —
        * custom themes included — like the wash family. With just the
        * workspace mark up top the slot keeps its height but paints no line —
        * the breath stays, the rung goes. */}
      {headDividerSlot && (
        <Box align="center" fill="width" paddingY="xs">
          {showHeadDividerLine ? (
            <Separator className="w-4 bg-foreground/10" />
          ) : (
            <Box height={1} />
          )}
        </Box>
      )}

      {visibleTiles.map((tile, index) => {
        // Home shows the live inbox unread count. Suppress it while Home
        // is the active tile AND its module menu is visible — the module
        // menu's own Inbox row carries the count there. With the module menu
        // collapsed that row is hidden, so the rail badge stays on even on
        // Home.
        const badge =
          tile.id === "home" && (activeTileId !== "home" || submenuCollapsed) && inboxUnreadCount > 0
            ? String(inboxUnreadCount)
            : tile.badge;
        const railTile = (
          <AppShellRailTile
            active={tile.id === activeTileId}
            badge={badge}
            badgeDot={countDot}
            compact={compact}
            icon={tile.icon}
            label={tile.label}
            onClick={() => handleTilePress(tile)}
            selectedIcon={tile.selectedIcon}
            size="lg"
          />
        );
        // With the pane away and a pointer that can hover, a module tile
        // previews its whole menu in the shared flyout — browse without
        // committing. The tile only reports hover intent to the ONE popup
        // below; it replaces the name tooltip, since the menu's own pane
        // title already names the module.
        if (menuFlyout && (tile.hasSubmenu ?? true)) {
          return React.cloneElement(railTile, {
            key: `${tile.id}-${index}`,
            ref: (el: HTMLElement | null) => tileEls.current.set(tile.id, el),
            onPointerEnter: () => enterFlyoutTile(tile.id),
            onPointerLeave: scheduleFlyoutClose,
            // Keyboard parity with the old focus-opened preview cards.
            onFocus: () => enterFlyoutTile(tile.id),
            onBlur: scheduleFlyoutClose,
          });
        }
        return (
          // Every rail item names itself on hover, to the RIGHT — into the
          // page, where a bubble has room. A caption is not a substitute: it
          // is gone in compact mode, and it carries the module's short name
          // while the tooltip can carry the full one.
          //
          // "Go to" because these tiles NAVIGATE. It is the same split the
          // captions draw: a destination says where it takes you, an action
          // (Invite, More, search, the identity marks) says what it does.
          <Tooltip key={`${tile.id}-${index}`}>
            <TooltipTrigger render={railTile} />
            <TooltipPopup side="right">
              <ModuleTooltipBody hotkey={tile.hotkey} label={tile.label} />
            </TooltipPopup>
          </Tooltip>
        );
      })}

      {/* The shared module-menu flyout — anchored to the RAIL, not a tile, so
        * the panel holds ONE static spot while the pointer walks the column
        * (ClickUp's arrangement). Controlled by the hover-intent state above:
        * one popup for every tile is what lets a tile change swap the menu in
        * place instead of closing and re-opening the panel. */}
      {menuFlyout && (
        <PreviewCard
          onOpenChange={(open, details) => {
            if (open) return;
            // A press on the rail is a module press, not a dismissal: the
            // tiles are not registered triggers, so Base UI's outside-press
            // dismissal would close the panel under the pointer and the
            // tile's focus would re-open it after the open delay — a blink
            // on every click between modules. Hover state owns the panel
            // here; the panel keeps showing the pressed module's menu.
            if (
              details.reason === "outside-press" &&
              details.event.target instanceof Node &&
              railRef.current?.contains(details.event.target)
            ) {
              details.cancel();
              return;
            }
            // Base UI's other dismissals (Escape, a press into the page)
            // land here; hover routes through the tile/popup handlers.
            clearFlyoutTimers();
            setFlyoutTileId(null);
          }}
          open={flyoutTileId != null}
        >
          <PreviewCardPopup
            align="center"
            // A GETTER, not `railRef` itself: `useElementSize`'s ref is a
            // callback function (with a `.current` bolted on), and Base UI
            // treats a function anchor as a getter and CALLS it — invoking the
            // attach callback with no element and resolving no anchor, which
            // parks the popup unpositioned at (0,0): an invisible layer over
            // the rail that swallows every hover and click.
            anchor={() => railRef.current}
            // On touch the flyout sits over a dismiss layer, so the tap that
            // closes it presses nothing beneath; the rail stays tappable
            // through it, since a tile press is a module press.
            dismissCutout={() => railRef.current}
            // Keep 12px viewport insets, matching the rail-to-popup gap.
            // The caret needs to poke past the edge, so the
            // CONTENT clips (FlyoutPanel), not the popup.
            className="h-[calc(100dvh-24px)] w-[270px] p-0"
            collisionPadding={12}
            onPointerEnter={cancelFlyoutClose}
            onPointerLeave={scheduleFlyoutClose}
            side="right"
          >
            {flyoutContentTileId != null && (
              <FlyoutPanel tileEl={tileEls.current.get(flyoutContentTileId)}>
                {/* The settings pane is not a module menu, but it is the pane
                  * the Settings chip stands in for — the same swap the
                  * workspace layout makes on a settings route. */}
                {flyoutContentTileId === SETTINGS_BROWSE_ID ? (
                  <SettingsSidebar />
                ) : (
                  <ModuleMenuForTile tileId={flyoutContentTileId} />
                )}
              </FlyoutPanel>
            )}
          </PreviewCardPopup>
        </PreviewCard>
      )}

      {/* Divider between the configurable tiles and the fixed "More" tile —
        * the ref's short 16px rung, not a near-full-width line. Ink at alpha
        * for the same reason as the head divider above. */}
      <Box align="center" fill="width" paddingY="xs">
        <Separator className="w-4 bg-foreground/10" />
      </Box>

      {/* Measuring wrapper: the More tile is the one rail tile that ALWAYS
       * renders, so its height is the live per-tile slot height the fit
       * math uses (tracks compact mode and font changes automatically). */}
      <Box fill="width" ref={tileSlotRef}>
        <MoreModulesTile
          compact={compact}
          onInvitePress={
            canInvite && !inviteInRail ? () => setInviteOpen(true) : undefined
          }
          onTilePress={handleTilePress}
          overflowTiles={overflowTiles}
          popoverTopInset={railTop}
        />
      </Box>

      {/* Spacer pushes the pinned bottom items down */}
      <Box fill="flex" />

      {/* Pinned bottom: Invite (permission-gated). When the column is too
       * short for its slot it moves into the More popup instead.
       *
       * Uncaptioned in both rail modes, unlike the module tiles above it: this
       * is an ACTION, not a destination. A destination has to announce itself
       * to someone who has not been there — an action at the foot of a toolbar
       * is conventionally a bare glyph with a tooltip, and `user-round-plus` carries
       * "invite someone" without help. It also keeps the foot of the rail a
       * cluster of bare chips instead of putting a caption directly on top of
       * the account mark. */}
      {canInvite && inviteInRail && (
        <Tooltip>
          <TooltipTrigger
            render={
              <AppShellRailTile
                compact
                icon="user-plus"
                label="Invite"
                onClick={() => setInviteOpen(true)}
                size="lg"
              />
            }
          />
          <TooltipPopup side="right">Invite</TooltipPopup>
        </Tooltip>
      )}
      {/* Foot: the account mark closes the column — the desktop shell's only
        * account surface, since there is no site header to carry it. A
        * divider-sized breath seats it apart from the action chips above —
        * the account is not the next item in a list, it is the end of the
        * column. Space only, no line, mirroring the head slot when just the
        * workspace mark is up. */}
      {showIdentity && (
        <>
          <Box align="center" fill="width" paddingY="xs">
            <Box height={1} />
          </Box>
          <Box fill="width">
            <AccountMenu variant="rail" />
          </Box>
        </>
      )}

      {canInvite && <InviteMembersDialog open={inviteOpen} onOpenChange={setInviteOpen} />}
    </Box>
  );
}
